import React, { useState } from 'react';
import axios from 'axios';
import SignIn from './components/SignIn';
import Shell from './components/Shell';
import PracticeMode from './components/PracticeMode';
import MiniMockExam from './components/MiniMockExam';
import FullMockExam from './components/FullMockExam';
import ResultsScreen from './components/ResultsScreen';
import './App.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

// Registered once at module load, before any component mounts or runs its
// own effects — attaching the header inside a component useEffect raced
// against child components' own data-fetching effects (which run first in
// React's child-before-parent effect order), so the first authenticated
// request after every page load went out with no Authorization header.
axios.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// A 403 usually means the session itself is no longer valid — an invalid/
// expired token, or an admin revoking access (unenroll / access_until
// cutoff) mid-session — except "Admin access required", which is a normal,
// recoverable permission check that shouldn't force a sign-out. Everything
// else forces back to the sign-in screen with the server's own explanation.
axios.interceptors.response.use(
  (res) => res,
  (err) => {
    const status = err.response?.status;
    const message = err.response?.data?.error || '';
    if (status === 403 && message !== 'Admin access required') {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      sessionStorage.setItem('authNotice', message);
      window.location.href = '/';
    }
    return Promise.reject(err);
  }
);

function App() {
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [user, setUser] = useState(JSON.parse(localStorage.getItem('user')));
  const [currentPage, setCurrentPage] = useState('dashboard');
  const [practiceMode, setPracticeMode] = useState(null);
  const [mockExamData, setMockExamData] = useState(null);
  const [results, setResults] = useState(null);
  // Lives here, not inside Shell, so it survives Shell being fully unmounted
  // while a practice session or mock exam is in progress (currentPage
  // switches away from 'dashboard') — returning via onBack lands back on
  // whichever sidebar tab was last active instead of resetting to Home.
  const [shellNav, setShellNav] = useState({ section: 'home', sub: null });

  const handleSignIn = (authToken, userData) => {
    localStorage.setItem('token', authToken);
    localStorage.setItem('user', JSON.stringify(userData));
    setToken(authToken);
    setUser(userData);
    setCurrentPage('dashboard');
  };

  const handleSignOut = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    setToken(null);
    setUser(null);
    setCurrentPage('signin');
    setPracticeMode(null);
    setMockExamData(null);
    setResults(null);
  };

  const handleStartPractice = (section, filters) => {
    setPracticeMode({ section, filters });
    setCurrentPage('practice');
  };

  const handleStartMiniMock = (mockId) => {
    setMockExamData({ type: 'mini', id: mockId });
    setCurrentPage('mini-mock');
  };

  const handleStartFullMock = (mockId) => {
    setMockExamData({ type: 'full', id: mockId });
    setCurrentPage('full-mock');
  };

  const handleShowResults = (resultData) => {
    setResults(resultData);
    setCurrentPage('results');
  };

  const handleShowMyResults = () => {
    setShellNav({ section: 'my-results', sub: null });
    setCurrentPage('dashboard');
  };

  const renderPage = () => {
    if (!token) {
      return <SignIn onSignIn={handleSignIn} apiUrl={API_URL} />;
    }

    switch (currentPage) {
      case 'dashboard':
        return (
          <Shell
            user={user}
            onSignOut={handleSignOut}
            onStartPractice={handleStartPractice}
            onStartMiniMock={handleStartMiniMock}
            onStartFullMock={handleStartFullMock}
            apiUrl={API_URL}
            nav={shellNav}
            setNav={setShellNav}
          />
        );
      case 'practice':
        return (
          <PracticeMode
            section={practiceMode.section}
            initialFilters={practiceMode.filters}
            user={user}
            onBack={() => setCurrentPage('dashboard')}
            onShowResults={handleShowResults}
            apiUrl={API_URL}
          />
        );
      case 'mini-mock':
        return (
          <MiniMockExam
            mockId={mockExamData.id}
            user={user}
            onBack={() => setCurrentPage('dashboard')}
            onShowResults={handleShowResults}
            apiUrl={API_URL}
          />
        );
      case 'full-mock':
        return (
          <FullMockExam
            mockId={mockExamData.id}
            user={user}
            onBack={() => setCurrentPage('dashboard')}
            onShowResults={handleShowResults}
            apiUrl={API_URL}
          />
        );
      case 'results':
        return (
          <ResultsScreen
            results={results}
            onBack={() => setCurrentPage('dashboard')}
            onViewAllResults={handleShowMyResults}
            onRetake={() => {
              if (results.type === 'practice') handleStartPractice(results.section);
              else if (results.type === 'mini-mock') handleStartMiniMock(results.attempt.mini_mock_id);
              else handleStartFullMock(results.attempt.full_mock_id);
            }}
          />
        );
      default:
        return (
          <Shell
            user={user}
            onSignOut={handleSignOut}
            onStartPractice={handleStartPractice}
            onStartMiniMock={handleStartMiniMock}
            onStartFullMock={handleStartFullMock}
            apiUrl={API_URL}
            nav={shellNav}
            setNav={setShellNav}
          />
        );
    }
  };

  return <div className="app">{renderPage()}</div>;
}

export default App;
